import { useRef, useState, useEffect, useCallback } from "react";
import { useBoard, boardStore } from "@/lib/boardStore";
import EvidenceCard from "@/components/board/EvidenceCard";
import YarnLayer from "@/components/board/YarnLayer";

const CARD_WIDTH = 220;
const CARD_HEIGHT_ESTIMATE = 260;

export default function Board({ onEditCard }) {
  const { cards, connections, categories, locked } = useBoard();
  const containerRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const panState = useRef({ panning: false, startX: 0, startY: 0, vx: 0, vy: 0 });
  const [draggingId, setDraggingId] = useState(null);
  const dragState = useRef({ id: null, offsetX: 0, offsetY: 0 });
  const pinDragState = useRef({ connId: null, pinIndex: -1 });
  const [cardSizes, setCardSizes] = useState({});

  const registerCardSize = useCallback((id, w, h) => {
    setCardSizes((prev) => {
      const cur = prev[id];
      if (cur && cur.w === w && cur.h === h) return prev;
      return { ...prev, [id]: { w, h } };
    });
  }, []);

  const onWheel = (e) => {
    if (!containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const delta = -e.deltaY * 0.0015;
    const newScale = Math.max(0.3, Math.min(2.5, view.scale * (1 + delta)));
    const wx = (mx - view.x) / view.scale;
    const wy = (my - view.y) / view.scale;
    const nx = mx - wx * newScale;
    const ny = my - wy * newScale;
    setView({ x: nx, y: ny, scale: newScale });
  };

  const onMouseDown = (e) => {
    // if (e.target !== e.currentTarget && !e.target.dataset?.boardBg) return;
    if (e.button !== 0 && e.button !== 1) return;
    panState.current = {
      panning: true,
      startX: e.clientX,
      startY: e.clientY,
      vx: view.x,
      vy: view.y,
    };
    document.body.style.cursor = "grabbing";
  };

useEffect(() => {
  const PAN_SPEED = 20; // Adjust pixel movement per keypress

  const onMove = (e) => {
    if (panState.current.panning) {
      setView((v) => ({
        ...v,
        x: panState.current.vx + (e.clientX - panState.current.startX),
        y: panState.current.vy + (e.clientY - panState.current.startY),
      }));
    }
    if (dragState.current.id) {
      const rect = containerRef.current.getBoundingClientRect();
      const wx = (e.clientX - rect.left - view.x) / view.scale;
      const wy = (e.clientY - rect.top - view.y) / view.scale;
      boardStore.moveCard(
        dragState.current.id,
        wx - dragState.current.offsetX,
        wy - dragState.current.offsetY
      );
    }
    if (pinDragState.current.connId) {
      const rect = containerRef.current.getBoundingClientRect();
      const wx = (e.clientX - rect.left - view.x) / view.scale;
      const wy = (e.clientY - rect.top - view.y) / view.scale;
      boardStore.movePin(
        pinDragState.current.connId,
        pinDragState.current.pinIndex,
        wx,
        wy
      );
    }
  };

  const onUp = () => {
    if (panState.current.panning) {
      panState.current.panning = false;
      document.body.style.cursor = "";
    }
    if (dragState.current.id) {
      dragState.current = { id: null, offsetX: 0, offsetY: 0 };
      setDraggingId(null);
    }
    if (pinDragState.current.connId) {
      pinDragState.current = { connId: null, pinIndex: -1 };
    }
  };

  const onKeyDown = (e) => {
    // Ignore arrow key panning if typing inside text fields/inputs
    const activeEl = document.activeElement;
    const isTyping =
      activeEl &&
      (activeEl.tagName === "INPUT" ||
        activeEl.tagName === "TEXTAREA" ||
        activeEl.isContentEditable);

    if (isTyping) return;

    let dx = 0;
    let dy = 0;

    switch (e.key) {
      case "ArrowUp":
        dy = PAN_SPEED; // Shift view down (canvas content moves down)
        break;
      case "ArrowDown":
        dy = -PAN_SPEED; // Shift view up
        break;
      case "ArrowLeft":
        dx = PAN_SPEED; // Shift view right
        break;
      case "ArrowRight":
        dx = -PAN_SPEED; // Shift view left
        break;
      default:
        return; // Exit if not an arrow key
    }

    e.preventDefault(); // Prevent default browser scroll behavior

    setView((v) => ({
      ...v,
      x: v.x + dx,
      y: v.y + dy,
    }));
  };

  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
  window.addEventListener("keydown", onKeyDown);

  return () => {
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
    window.removeEventListener("keydown", onKeyDown);
  };
}, [view.scale, view.x, view.y]);

  const startCardDrag = (e, card) => {
    if (locked) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    const wx = (e.clientX - rect.left - view.x) / view.scale;
    const wy = (e.clientY - rect.top - view.y) / view.scale;
    dragState.current = {
      id: card.id,
      offsetX: wx - card.x,
      offsetY: wy - card.y,
    };
    setDraggingId(card.id);
  };

  const startPinDrag = (connId, pinIndex) => {
    if (locked) return;
    pinDragState.current = { connId, pinIndex };
  };

  const resetView = () => setView({ x: 0, y: 0, scale: 1 });
  const zoomIn = () =>
    setView((v) => ({ ...v, scale: Math.min(2.5, v.scale * 1.15) }));
  const zoomOut = () =>
    setView((v) => ({ ...v, scale: Math.max(0.3, v.scale / 1.15) }));

  const categoryOf = (id) => categories.find((c) => c.id === id);

  const cardCenters = {};
  cards.forEach((c) => {
    const size = cardSizes[c.id] || { w: CARD_WIDTH, h: CARD_HEIGHT_ESTIMATE };
    cardCenters[c.id] = { x: c.x + size.w / 2, y: c.y + size.h / 2 };
  });

  return (
    <>
      <div
        ref={containerRef}
        data-board-bg="true"
        data-testid="board-canvas"
        className="absolute inset-0 z-0 overflow-hidden cursor-grab active:cursor-grabbing"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
      >
        <div
          className="absolute top-0 left-0 origin-top-left"
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transformOrigin: "0 0",
            width: 1,
            height: 1,
          }}
        >
          <YarnLayer
            connections={connections}
            centers={cardCenters}
            sizes={cardSizes}
            cards={cards}
            locked={locked}
            onStartPinDrag={startPinDrag}
          />
          {cards.map((card) => (
            <EvidenceCard
              key={card.id}
              card={card}
              category={categoryOf(card.categoryId)}
              locked={locked}
              dragging={draggingId === card.id}
              onDragStart={(e) => startCardDrag(e, card)}
              onEdit={() => onEditCard(card.id)}
              registerSize={registerCardSize}
            />
          ))}
        </div>

        {cards.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="text-center max-w-md px-8">
              <div className="noir-title text-4xl text-[#f4d9a8] mb-3">
                The Board is Empty
              </div>
              <div className="typewriter text-[#c9b285]/70 text-sm leading-relaxed">
                Pin a node. Connect the dots. Follow the thread.
                <br />
                Hit <span className="text-[#e0b070]">+ New Node</span> to begin
                your investigation.
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-4 right-4 z-30 flex flex-col gap-1 bg-[#1a140c]/85 backdrop-blur-md border border-[#5c4426]/40 rounded-md p-1 shadow-lg">
        <button
          data-testid="zoom-in-btn"
          onClick={zoomIn}
          className="w-9 h-9 flex items-center justify-center text-[#f4d9a8] hover:bg-[#5c4426]/40 rounded text-lg leading-none"
          title="Zoom in"
        >
          +
        </button>
        <button
          data-testid="zoom-reset-btn"
          onClick={resetView}
          className="w-9 h-8 flex items-center justify-center text-[#f4d9a8] hover:bg-[#5c4426]/40 rounded text-[10px] typewriter"
          title="Reset view"
        >
          {Math.round(view.scale * 100)}%
        </button>
        <button
          data-testid="zoom-out-btn"
          onClick={zoomOut}
          className="w-9 h-9 flex items-center justify-center text-[#f4d9a8] hover:bg-[#5c4426]/40 rounded text-lg leading-none"
          title="Zoom out"
        >
          −
        </button>
      </div>
    </>
  );
}
